
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const MountainPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/b8308ba2348786dadab6c18090162c8e.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Mountain Retreats</h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            Experience the majesty of towering peaks and pristine alpine wilderness in our luxury mountain glamping retreats
          </p>
          <Link 
            to="/book" 
            className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            Book Mountain Experience
          </Link>
        </div>
      </section>

      {/* Experience Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Alpine Adventure Awaits</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our mountain retreats offer unparalleled access to some of the most spectacular alpine environments. 
                Wake up to panoramic views of snow-capped peaks, breathe the crisp mountain air, and immerse yourself 
                in the tranquility of high-altitude wilderness.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Each mountain location features luxury accommodations designed to complement the natural landscape, 
                with floor-to-ceiling windows, private decks, and premium amenities that ensure comfort at altitude.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">8,500ft</div>
                  <div className="text-gray-600">Average Elevation</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">12</div>
                  <div className="text-gray-600">Mountain Locations</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20glamping%20tent%20on%20mountain%20ridge%20with%20panoramic%20alpine%20views%2C%20modern%20eco-friendly%20accommodation%20with%20large%20windows%2C%20wooden%20deck%20overlooking%20valley%2C%20snow-capped%20peaks%20in%20background%2C%20premium%20outdoor%20furniture%2C%20sustainable%20mountain%20architecture&width=600&height=400&seq=mountain-glamp&orientation=landscape"
                alt="Mountain Glamping"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Mountain Activities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From challenging hikes to peaceful meditation, our mountain retreats offer activities for every adventure level
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-mountain-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Alpine Hiking</h3>
              <p className="text-gray-700 leading-relaxed">
                Explore pristine mountain trails with guided hikes ranging from gentle nature walks to challenging summit attempts.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-camera-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Photography Tours</h3>
              <p className="text-gray-700 leading-relaxed">
                Capture stunning mountain vistas with professional photography workshops during golden hour and blue hour.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Wellness Retreats</h3>
              <p className="text-gray-700 leading-relaxed">
                Practice yoga and meditation in serene alpine settings, with fresh mountain air enhancing your wellness journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Locations */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Mountain Locations</h2>
            <p className="text-xl text-gray-600">Discover our most spectacular mountain retreat destinations</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Rocky%20Mountain%20peaks%20with%20luxury%20glamping%20site%2C%20dramatic%20granite%20cliffs%20and%20alpine%20lakes%2C%20evergreen%20forests%2C%20golden%20sunset%20lighting%20on%20mountain%20faces%2C%20premium%20eco-lodge%20accommodation%20with%20mountain%20views&width=600&height=300&seq=rocky-mountains&orientation=landscape"
                alt="Rocky Mountain Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Rocky Mountain Heights</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Experience the grandeur of the Rocky Mountains with panoramic views, alpine lakes, and world-class hiking trails.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Colorado, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Cascade%20mountain%20range%20with%20luxury%20glamping%20retreat%2C%20snow-capped%20volcanic%20peaks%2C%20dense%20forest%20valleys%2C%20pristine%20wilderness%20setting%2C%20modern%20sustainable%20accommodation%20blending%20with%20natural%20environment&width=600&height=300&seq=cascade-mountains&orientation=landscape"
                alt="Cascade Peaks Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Cascade Peaks</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Immerse yourself in the volcanic beauty of the Cascade Range with glacier-fed streams and ancient forests.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Washington, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-teal-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready for Your Mountain Adventure?</h2>
          <p className="text-xl text-teal-100 mb-8 leading-relaxed">
            Book your mountain retreat today and experience the ultimate in alpine luxury and natural beauty
          </p>
          <Link 
            to="/book" 
            className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
          >
            Book Your Mountain Retreat
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MountainPage;
